import type { HeroDefinition, HeroId, TileColor } from '../types';
import { signatureTune, tunedCells } from './signature';

interface CastCard {
  id: HeroId;
  name: string;
  species: string;
  classId: HeroDefinition['classId'];
  powerName: string;
  color: TileColor;
  description: string;
  target: HeroDefinition['target'];
}

const CAST: readonly CastCard[] = [
  {
    id: 'bruno',
    name: 'Bruno',
    species: 'bear cub',
    classId: 'guardian',
    powerName: 'Skillet Slam',
    color: 'honey',
    description: 'Clears a row, plus the tile his skillet lands beside.',
    target: 'row',
  },
  {
    id: 'tuck',
    name: 'Tuck',
    species: 'tortoise',
    classId: 'guardian',
    powerName: 'Plank Guard',
    color: 'leaf',
    description: 'Clears the middle of a row. The ends stay put.',
    target: 'row',
  },
  {
    id: 'thistle',
    name: 'Thistle',
    species: 'badger',
    classId: 'infiltration',
    powerName: 'Briar Cut',
    color: 'blossom',
    description: 'Clears a row or a column, leaving both ends.',
    target: 'line',
  },
  {
    id: 'marlow',
    name: 'Marlow',
    species: 'mole',
    classId: 'infiltration',
    powerName: 'Deep Dig',
    color: 'acorn',
    description: 'Clears a row or a column, and one tile beside the middle.',
    target: 'line',
  },
  {
    id: 'wren',
    name: 'Wren',
    species: 'owl',
    classId: 'stealth',
    powerName: 'Moon Glare',
    color: 'moonstone',
    description: 'Turns a scatter of tiles into moonstones, leaned toward a tap.',
    target: 'area',
  },
  {
    id: 'juniper',
    name: 'Juniper',
    species: 'raccoon',
    classId: 'stealth',
    powerName: 'Mask Dust',
    color: 'berry',
    description: 'Turns a wide scatter of tiles into berries, leaned toward a tap.',
    target: 'area',
  },
  {
    id: 'lumi',
    name: 'Lumi',
    species: 'swanling',
    classId: 'tidecaller',
    powerName: 'Glide',
    color: 'blossom',
    description: 'Clears two lines, one band in from the edge you aim.',
    target: 'wave',
  },
  {
    id: 'barley',
    name: 'Barley',
    species: 'beaver',
    classId: 'tidecaller',
    powerName: 'Dam Burst',
    color: 'acorn',
    description: 'Clears two edge lines, leaving the corners.',
    target: 'wave',
  },
  {
    id: 'fern',
    name: 'Fern',
    species: 'dormouse',
    classId: 'grove',
    powerName: 'Fernbed',
    color: 'blossom',
    description: 'Turns a scatter of tiles into blossoms, leaned toward a tap.',
    target: 'area',
  },
  {
    id: 'posy',
    name: 'Posy',
    species: 'bunny',
    classId: 'grove',
    powerName: 'Petal Patch',
    color: 'blossom',
    description: 'Turns a small scatter of tiles into blossoms, leaned toward a tap.',
    target: 'area',
  },
  {
    id: 'dash',
    name: 'Dash',
    species: 'squirrel',
    classId: 'grove',
    powerName: 'Canopy Toss',
    color: 'honey',
    description: 'Turns a scatter of tiles into blossoms, leaned toward a tap.',
    target: 'area',
  },
];

function define(card: CastCard): HeroDefinition {
  return {
    ...card,
    paw: `cc_hero_${card.id}_paw@1x.png`,
    apply(state, _player, target, aim) {
      const tune = signatureTune(card.id);
      const cells = tunedCells(state, card.id, { target, axis: aim?.axis, dir: aim?.dir }) ?? [];
      if (tune?.kind === 'bloom') {
        return {
          clear: [],
          convert: cells.map((position) => ({ position, color: tune.color })),
        };
      }
      return { clear: cells, convert: [] };
    },
  };
}

export const bruno = define(CAST[0]);
export const tuck = define(CAST[1]);
export const thistle = define(CAST[2]);
export const marlow = define(CAST[3]);
export const wren = define(CAST[4]);
export const juniper = define(CAST[5]);
export const lumi = define(CAST[6]);
export const barley = define(CAST[7]);
export const fern = define(CAST[8]);
export const posy = define(CAST[9]);
export const dash = define(CAST[10]);

export const NEW_CAST: readonly HeroDefinition[] = [
  bruno,
  tuck,
  thistle,
  marlow,
  wren,
  juniper,
  lumi,
  barley,
  fern,
  posy,
  dash,
];
